"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, Search, ImagePlay, X, Sparkles } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogTitle,
} from "@/components/ui/responsive-dialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { searchGiphy, type GiphyGif } from "@/lib/giphy.functions";
import { cn } from "@/lib/utils";

const SUGGESTIONS = [
  "reação",
  "amei",
  "risada",
  "chocado",
  "vinil",
  "dançando",
  "aplausos",
  "coração",
  "triste",
  "confuso",
];

function useDebounced<T>(value: T, delay = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

function GifGrid({
  gifs,
  loading,
  columns,
  onPick,
}: {
  gifs: GiphyGif[];
  loading: boolean;
  columns: number;
  onPick: (gif: GiphyGif) => void;
}) {
  if (loading) {
    return (
      <div className="grid place-items-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (gifs.length === 0) {
    return (
      <div className="grid place-items-center gap-2 py-16 text-center">
        <Sparkles className="h-5 w-5 text-muted-foreground" />
        <p className="text-xs text-muted-foreground">Nenhum GIF encontrado</p>
      </div>
    );
  }
  // CSS masonry columns preserve GIF aspect ratios nicely.
  return (
    <div
      className="gap-1.5"
      style={{ columnCount: columns, columnGap: 6 }}
    >
      {gifs.map((g) => (
        <button
          key={g.id}
          type="button"
          onClick={() => onPick(g)}
          className="group mb-1.5 block w-full overflow-hidden rounded-lg bg-surface transition-transform active:scale-[0.98]"
          style={{ breakInside: "avoid" }}
        >
          <img
            src={g.url}
            alt=""
            loading="lazy"
            className="h-auto w-full object-cover transition-transform group-hover:scale-[1.02]"
            style={{ aspectRatio: `${g.width} / ${g.height || 1}` }}
          />
        </button>
      ))}
    </div>
  );
}

function GifPickerBody({
  onPick,
  onDone,
  columns,
}: {
  onPick: (gif: { url: string }) => void;
  onDone: () => void;
  columns: number;
}) {
  const [query, setQuery] = useState("");
  const debounced = useDebounced(query, 300);
  const searchFn = useServerFn(searchGiphy);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const q = useQuery({
    queryKey: ["giphy", debounced],
    queryFn: () => searchFn({ data: { query: debounced } }),
    staleTime: 60_000,
  });

  useEffect(() => {
    // Focus after mount (skip on mobile to avoid the keyboard popping instantly).
    if (typeof window !== "undefined" && window.innerWidth >= 640) {
      inputRef.current?.focus();
    }
  }, []);

  const gifs = useMemo(() => (q.data as GiphyGif[] | undefined) ?? [], [q.data]);
  const isTrending = !debounced.trim();

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center gap-2 border-b border-[color:var(--hairline)] px-3 pb-3 pt-2 sm:pt-3">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar no GIPHY"
            enterKeyHint="search"
            className="h-11 w-full rounded-full border border-[color:var(--hairline)] bg-surface/60 pl-9 pr-9 text-[15px] focus:border-primary/60 focus:outline-none sm:h-9 sm:text-sm"
            style={{ fontSize: "16px" }}
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              aria-label="Limpar busca"
              className="absolute right-2 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-full text-muted-foreground hover:bg-surface hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {isTrending && (
        <div className="flex gap-1.5 overflow-x-auto px-3 py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {SUGGESTIONS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setQuery(s)}
              className="shrink-0 rounded-full border border-[color:var(--hairline)] bg-surface/60 px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
            >
              {s}
            </button>
          ))}
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-2">
        {isTrending && (
          <p className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Em alta agora
          </p>
        )}
        <GifGrid
          gifs={gifs}
          loading={q.isLoading}
          columns={columns}
          onPick={(g) => {
            onPick({ url: g.full_url });
            onDone();
          }}
        />
      </div>

      <div className="border-t border-[color:var(--hairline)] px-3 py-1.5 text-center text-[10px] uppercase tracking-wider text-muted-foreground">
        Powered by GIPHY
      </div>
    </div>
  );
}

export function GifPicker({
  onPick,
  className,
  size = "sm",
}: {
  onPick: (gif: { url: string }) => void;
  className?: string;
  size?: "xs" | "sm";
}) {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();

  const dim = size === "xs" ? "h-7 w-7" : "h-9 w-9";

  const Trigger = (
    <button
      type="button"
      aria-label="Escolher GIF"
      onClick={() => setOpen(true)}
      className={cn(
        "grid place-items-center rounded-full text-muted-foreground transition-colors hover:bg-surface hover:text-foreground",
        dim,
        className,
      )}
    >
      <ImagePlay className={size === "xs" ? "h-3.5 w-3.5" : "h-4 w-4"} />
    </button>
  );

  if (isMobile) {
    return (
      <>
        {Trigger}
        <ResponsiveDialog open={open} onOpenChange={setOpen}>
          <ResponsiveDialogContent className="h-[82dvh] p-0" hideClose>
            <ResponsiveDialogTitle className="sr-only">Escolher GIF</ResponsiveDialogTitle>
            <div className="flex h-full min-h-0 flex-col">
              {open && (
                <GifPickerBody
                  onPick={onPick}
                  onDone={() => setOpen(false)}
                  columns={2}
                />
              )}
            </div>
          </ResponsiveDialogContent>
        </ResponsiveDialog>
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Escolher GIF"
          className={cn(
            "grid place-items-center rounded-full text-muted-foreground transition-colors hover:bg-surface hover:text-foreground",
            dim,
            className,
          )}
        >
          <ImagePlay className={size === "xs" ? "h-3.5 w-3.5" : "h-4 w-4"} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="top"
        sideOffset={8}
        className="z-[60] flex h-[460px] w-[420px] flex-col overflow-hidden rounded-2xl border border-[color:var(--hairline)] bg-background p-0"
      >
        {open && (
          <GifPickerBody
            onPick={onPick}
            onDone={() => setOpen(false)}
            columns={3}
          />
        )}
      </PopoverContent>
    </Popover>
  );
}
